import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Card,
    FormControl,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Toolbar,
    Typography,
    Tooltip,
    Checkbox,
    ListItem,
    ListItemIcon,
    makeStyles,
    createStyles,
    Box,
} from '@material-ui/core';
import i18n from 'i18n-js';
import { images } from 'assets';
import { default as ordersStore } from 'store/ordersStore';
import { dateTimeIgnoreTimezone, formatDate, formatTime } from 'helpers/datetimeHelper';
import { OrderItemStatus, OrderServices, Positions } from 'constants/enums';
import { NothingFoundMessage, DateTimePicker } from 'components/shared';
import { ApplicationState } from 'store';
import { pagingOptions } from 'constants/arrays';
import AddNewOrderModal from './AddNewOrderModal';
import agent from 'api/agent';
import OrdersListStatus from './OrdersListStatus';
import { theme } from 'config/theme';
import { helperFunctions } from 'helpers';
import { removeWhiteSpace, truncateString } from 'helpers/helperFunctions';
import { services } from 'constants/maps';
import { useStyles } from './styles';
import { signalRService, SignalREvent } from 'services/signalR';
import WorkAreaSelector from './WorkAreaSelector';
import { CategoryDetails } from 'store/categoryStore/reducer';
import { OrderDetails } from 'store/ordersStore/reducer';
import { actionCreators } from 'store/ordersStore/actionCreators';
import clsx from 'clsx';
import commonStyles from 'config/commonStyles';
import { OrdersListSkeleton } from '../OrdersSkeletons';
import { isOrdersListLoading } from '../ordersLoading';

interface PageModel {
    page: number;
    rowsPerPage: number;
}

interface OrdersListProps {
    onOrderSelected?: (orderIds: string[]) => void;
    selectedOrders?: string[];
    timeOnly?: boolean;
    workAreas: CategoryDetails[] | undefined;
    selectedWorkAreas?: CategoryDetails[] | undefined;
    handleCategory: (category: CategoryDetails) => void;
    skipFirstLoad?: boolean;
}

const styles = makeStyles((theme) =>
    createStyles({
        imageContainer: {
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            height: 24,
            width: 24,
            transition: 'transform 0.5s',
        },
        collapsedIconContainer: {
            ...commonStyles.flexRowCenter,
            cursor: 'pointer',
            height: 34,
            width: 34,
            boxShadow: '0px 0px 7px rgba(140, 121, 133, 0.3)',
            borderRadius: 4,
            minWidth: 'unset',
        },
        collapsedIcon: {
            transform: 'rotate(180deg)',
        },
        ordersTableTransition: {
            transition: 'flex 0.5s',
            flexGrow: 0,
            flexShrink: 0,
            maxWidth: '30%',
        },
        ordersTableTransitionCook: {
            maxWidth: 'fit-content',
        },
        ordersTableCollapsed: {
            flexGrow: 0,
        },
    })
);

export default function OrdersListTable(props: OrdersListProps) {
    const classes = Object.assign({}, useStyles(), styles());
    const dispatch = useDispatch();
    const orders = useSelector((state: ApplicationState) => state.orders);
    const user = useSelector((state: ApplicationState) => state.user);
    const navBarOpen = useSelector((state: ApplicationState) => state.ui.navBarOpen);
    const currentPosition = useSelector(
        (state: ApplicationState) =>
            state.user.restaurants.find((rest) => rest.id === state.user.currentRestaurantId)
                ?.currentPosition
    );
    const categories = useSelector((state: ApplicationState) => state.category.cookCategories);
    const newOrdersCount = useSelector((state: ApplicationState) => state.orders.newOrders);

    const [paging, setPaging] = useState<PageModel>({ page: 0, rowsPerPage: pagingOptions[0] });
    const [showNewOrderModal, setShowNewOrderModal] = useState(false);
    const [readyDishes, setReadyDishes] = useState<{ [key: string]: string[] }>({});
    const [orderBy, setOrderBy] = useState('time');
    const [asc, setAsc] = useState(true);
    const [canCreateOrder, setCanCreateOrder] = useState(false);
    const [showClosed, setShowClosed] = useState(false);
    const getClosed = useRef(false);
    const currentPos = useRef<string | undefined>(undefined);

    //const [dateRange, setDateRange] = useState<[null, null] | [Date, Date]>([null, null]);
    const [showPicker, setShowPicker] = useState(false);
    const dateRange = useRef<[string | undefined, string | undefined]>([undefined, undefined]);
    const userRestaurantId = useRef('');
    const tableRef = useRef<HTMLDivElement>(null);
    const latestOrdersRef = useRef(orders.orders);
    const [isOpen, setOpen] = useState(true);
    const [skipFirstLoad, setSkipFirstLoad] = useState<boolean>(props.skipFirstLoad || false);

    function provideDestinationData(order: OrderDetails) {
        if (order.service === OrderServices.DineIn) {
            return truncateString(
                helperFunctions.removeWhiteSpace(order.tableName ?? order.qrCodeName),
                20
            );
        }
        if (order.service === OrderServices.TakeAway) {
            return i18n.t('common.pickup');
        }
        if (order.service === OrderServices.HomeDelivery) {
            return i18n.t('common.delivery');
        }
        if (order.service === OrderServices.TakeOut) {
            return i18n.t('restaurant.takeOut');
        }
    }

    function selectNewOrder(id: string | undefined) {
        if (id && props.onOrderSelected && props.selectedOrders) {
            if (props.selectedOrders.includes(id)) {
                const newSelectedOrders = props?.selectedOrders?.filter((eachId) => eachId !== id);
                return props.onOrderSelected(newSelectedOrders);
            }
            const newSelectedOrders = [...props.selectedOrders, id];
            return props.onOrderSelected(newSelectedOrders);
        }
    }

    function getOrdersList(
        sortByAsc: boolean = asc,
        orderByValue: string = orderBy,
        searchString: string = orders.searchString,
        pageNumber: number = paging.page,
        limit: number = paging.rowsPerPage
    ) {
        if (currentPos.current && !skipFirstLoad) {
            const position = currentPos.current === Positions.COOK ? Positions.COOK : '';
            dispatch(
                ordersStore.actionCreators.getOrdersList(
                    searchString,
                    dateRange.current[0],
                    dateRange.current[1],
                    pageNumber,
                    limit,
                    sortByAsc,
                    orderByValue,
                    position,
                    getClosed.current
                )
            );
        }
    }

    function handleSearch() {
        getOrdersList(asc, orderBy, orders.searchString, 0);
        setPaging({ ...paging, page: 0 });
    }

    function handleOrder(id: string) {
        if (orderBy === id) {
            setAsc(!asc);
        } else {
            setOrderBy(id);
        }
    }

    function formatedDate(date: number) {
        const newDate = dateTimeIgnoreTimezone(date);
        return `${formatTime(newDate)} ${!props.timeOnly ? formatDate(newDate) : ''}`;
    }

    function handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number
    ) {
        event?.stopPropagation();
        setPaging({ ...paging, page: newPage });
    }

    function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        setPaging({ ...paging, rowsPerPage: parseInt(event.target.value, 10), page: 0 });
    }

    function handleResetSearch() {
        dispatch(ordersStore.actionCreators.setShowSearch(false));
        dispatch(ordersStore.actionCreators.setSearchString(''));
        getOrdersList(asc, orderBy, '');
    }

    function submitDateRange(range: [Date, Date] | [null, null]) {
        const from = range[0]?.toISOString();
        const to = range[1]?.toISOString();
        dateRange.current = [from, to];
    }

    function addNewOrder(data: OrderDetails) {
        const isCookOrder = data.orderItems.some((i) =>
            categories.some(
                (c) => c.id === i.categoryId || i.dishes.some((d) => d.categoryId === c.id)
            )
        );
        if (currentPos.current === Positions.COOK && isCookOrder) {
            getOrdersList(asc, orderBy, orders.searchString, paging.page, paging.rowsPerPage);
        } else if (newOrdersCount + 1 < paging.page + 1 * paging.rowsPerPage) {
            dispatch(actionCreators.addNewOrder(data));
        } else if (currentPos.current !== Positions.COOK || isCookOrder) {
            dispatch(actionCreators.setOrdersCount(orders.count + 1));
        }
    }

    function updateOrder(data: OrderDetails) {
        dispatch(actionCreators.updateOneOrder(data, showClosed));
    }

    function filterByWorkAreas(order: OrderDetails) {
        if (props.selectedWorkAreas?.length === 0) {
            return false; // If no work areas are selected, show all orders
            // TODO: return false to show none AFTER BACK SET CATEGORY ID
        }
        return props.selectedWorkAreas?.some((area) =>
            order.orderItems.some((item) =>
                !!item.dishes?.length
                    ? item.dishes?.some((dish) => dish.categoryId === area.id)
                    : item.categoryId === area.id
            )
        );
    }

    function prepareTableBody() {
        let res = [];
        if (!!props.onOrderSelected) {
            res = orders.orders?.filter(filterByWorkAreas)?.map((order) => (
                <TableRow
                    key={order.id}
                    onClick={() => {
                        dispatch(
                            ordersStore.actionCreators.setOrderDetailsId(
                                orders.orderDetailsId !== order.id ? order.id : order.id + ' '
                            )
                        );
                    }}
                >
                    <TableCell align={'left'}>
                        <Checkbox
                            color={'primary'}
                            style={{ padding: 4, marginBottom: 2 }}
                            checked={props?.selectedOrders?.includes(order.id)}
                            onChange={() => selectNewOrder(order.id)}
                        />
                        <Tooltip
                            title={removeWhiteSpace(order.tableName ?? order.qrCodeName)}
                            placement="bottom-start"
                            disableHoverListener={
                                removeWhiteSpace(order.tableName ?? order.qrCodeName).length < 20
                            }
                        >
                            <span>{provideDestinationData(order)}</span>
                        </Tooltip>
                    </TableCell>
                    {isOpen && (
                        <TableCell
                            style={{
                                padding: '9px 9px 9px 16px',
                            }}
                            align={'left'}
                        >
                            {`№ ${order.orderNumber}`}
                        </TableCell>
                    )}

                    <TableCell className={classes.noPadding} align={'left'}>
                        {formatedDate(order.createDate)}
                    </TableCell>

                    {!navBarOpen && isOpen && (
                        <TableCell className={classes.noPadding} align={'center'}>
                            <img src={services.get(order.service)?.icon} alt="icon" height={22} />
                        </TableCell>
                    )}
                    {isOpen && (
                        <TableCell
                            style={{
                                padding: 9,
                            }}
                            align={'right'}
                        >
                            <OrdersListStatus
                                orderId={order.id}
                                showReady={
                                    readyDishes[order.id]?.length >
                                    order.orderItems.filter(
                                        (x) => x.status === OrderItemStatus.Ready
                                    ).length
                                }
                            />
                        </TableCell>
                    )}
                </TableRow>
            ));
        } else {
            res = orders.orders.map((order) => {
                const selected = order.id === orders.orderDetailsId;
                let selectedStyle, leftStyle, rightStyle;
                if (selected) {
                    selectedStyle = {
                        borderTop: `1px solid ${theme.palette.primary.main}`,
                        borderBottom: `1px solid ${theme.palette.primary.main}`,
                    };
                    leftStyle = {
                        borderLeft: `1px solid ${theme.palette.primary.main}`,
                    };
                    rightStyle = {
                        borderRight: `1px solid ${theme.palette.primary.main}`,
                    };
                }
                return (
                    <TableRow
                        style={{ cursor: 'pointer' }}
                        key={order.id}
                        onClick={() => {
                            dispatch(
                                ordersStore.actionCreators.setOrderDetailsId(
                                    orders.orderDetailsId !== order.id ? order.id : order.id + ' '
                                )
                            );
                        }}
                        className={selected ? classes.activeColor : ''}
                    >
                        <TableCell
                            style={{ ...leftStyle, ...selectedStyle }}
                            className={classes.noPadding}
                            align={'left'}
                        >
                            <Tooltip
                                title={removeWhiteSpace(order.tableName ?? order.qrCodeName)}
                                placement="bottom-start"
                                disableHoverListener={
                                    removeWhiteSpace(order.tableName ?? order.qrCodeName).length <
                                    20
                                }
                            >
                                <span>{provideDestinationData(order)}</span>
                            </Tooltip>
                        </TableCell>
                        {isOpen && (
                            <TableCell
                                style={{
                                    padding: '9px 9px 9px 16px',
                                    ...selectedStyle,
                                }}
                                align={'left'}
                            >
                                {`№ ${order.orderNumber}`}
                            </TableCell>
                        )}
                        <TableCell
                            style={{ ...selectedStyle, ...(!isOpen ? rightStyle : {}) }}
                            className={classes.noPadding}
                            align={'left'}
                        >
                            {formatedDate(order.createDate)}
                        </TableCell>

                        {!navBarOpen && isOpen && (
                            <TableCell
                                style={selectedStyle}
                                className={classes.noPadding}
                                align={'center'}
                            >
                                <img
                                    src={services.get(order.service)?.icon}
                                    alt="icon"
                                    height={22}
                                />
                            </TableCell>
                        )}
                        {isOpen && (
                            <TableCell
                                style={{
                                    padding: 9,
                                    ...selectedStyle,
                                    ...rightStyle,
                                }}
                                align={'right'}
                            >
                                <OrdersListStatus
                                    orderId={order.id}
                                    showReady={
                                        readyDishes[order.id]?.length >
                                        order.orderItems.filter(
                                            (x) => x.status === OrderItemStatus.Ready
                                        ).length
                                    }
                                />
                            </TableCell>
                        )}
                    </TableRow>
                );
            });
        }

        if (res.length > 0 && res.length < 13) {
            const neededRows = 12 - (res.length + 1);
            for (let i = 0; i < neededRows; i++) {
                res.push(
                    <TableRow key={`additional-row-${i}`} style={{ visibility: 'hidden' }}>
                        <TableCell className={classes.noPadding} align={'left'}>
                            0
                        </TableCell>
                    </TableRow>
                );
            }
        }

        return !!res.length ? (
            res
        ) : (
            <TableRow>
                <TableCell colSpan={6}>
                    <NothingFoundMessage
                        text={
                            orders.searchString
                                ? i18n.t('messages.weDidNotFindOrder', {
                                      order: orders.searchString,
                                  })
                                : undefined
                        }
                    />
                </TableCell>
            </TableRow>
        );
    }

    useEffect(() => {
        if (!user.currentRestaurantId) {
            agent.User.GetUserRestaurants().then((response) => {
                userRestaurantId.current = response.currentRestaurantId;
                dispatch({
                    type: 'USER_GET_RESTAURANTS_SUCCESS',
                    restaurants: response,
                });
            });
        } else {
            userRestaurantId.current = user.currentRestaurantId;
        }

        if (orders.searchString && orders.showSearch && orders.overdudedOrders.length > 0) {
            dispatch(
                ordersStore.actionCreators.getDetailedOverdudedOrders(orders.overdudedOrders, true)
            );
        } else {
            getOrdersList();
        }
    }, [dateRange.current]);

    useEffect(() => {
        return signalRService.subscribe(SignalREvent.OrderAdded, addNewOrder);
    }, []);

    useEffect(() => {
        return signalRService.subscribe(SignalREvent.OrderChanged, updateOrder);
    }, []);

    useEffect(() => {
        currentPos.current = currentPosition;
        setCanCreateOrder(currentPos.current !== Positions.COOK);
        getOrdersList();
    }, [currentPosition]);

    useEffect(() => {
        getClosed.current = showClosed;
        getOrdersList();
    }, [showClosed]);

    useEffect(() => {
        if (orders.searchString && orders.showSearch && orders.overdudedOrders.length > 0) {
            dispatch(
                ordersStore.actionCreators.getDetailedOverdudedOrders(orders.overdudedOrders, true)
            );
        } else {
            getOrdersList();
        }
    }, [paging.page, paging.rowsPerPage, orderBy, asc]);

    useEffect(() => {
        if (props.onOrderSelected) {
            props.onOrderSelected([]);
        }
    }, [paging.page]);

    useEffect(() => {
        latestOrdersRef.current = orders.orders;
    }, [orders.orders]);

    useEffect(() => {
        if (skipFirstLoad) {
            setSkipFirstLoad(false);
        }
    }, []);

    return (
        <Card
            className={clsx(classes.ordersTable, classes.ordersTableTransition, {
                [classes.ordersTableCollapsed]: !isOpen,
                [classes.ordersTableTransitionCook]: currentPosition === Positions.COOK,
            })}
        >
            <Toolbar className={classes.searchBar}>
                {orders.showSearch ? (
                    <FormControl fullWidth variant="standard">
                        <InputLabel htmlFor="filled-adornment-password">
                            {helperFunctions.truncateLangString(i18n.t('form.searchWord'), 22)}
                        </InputLabel>
                        <Input
                            id="filled-adornment-password"
                            type="text"
                            onChange={(e: any) =>
                                dispatch(
                                    ordersStore.actionCreators.setSearchString(
                                        e.target.value.trim()
                                    )
                                )
                            }
                            onKeyDown={(e: any) => e.key === 'Enter' && handleSearch()}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleResetSearch}
                                        edge="end"
                                    >
                                        <img
                                            src={images.icons.removeCircle}
                                            alt="remove-circle"
                                            className={classes.resetSearchIcon}
                                        />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                ) : (
                    <div className={classes.flexRowSpaceBetween}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {isOpen && (
                                <Typography variant="h6" id="tableTitle" component="div">
                                    {i18n.t('navigationTabs.orders')}
                                </Typography>
                            )}
                            {currentPosition === Positions.COOK ? (
                                <WorkAreaSelector
                                    workAreas={props.workAreas}
                                    selectedWorkAreas={props.selectedWorkAreas}
                                    handleCategory={props.handleCategory}
                                />
                            ) : null}
                            {!props.selectedOrders && (
                                <img
                                    src={images.icons.lens}
                                    className={classes.lensIcon}
                                    alt="lens-icon"
                                    onClick={() =>
                                        dispatch(ordersStore.actionCreators.setShowSearch(true))
                                    }
                                />
                            )}
                        </div>
                    </div>
                )}
                <div
                    className={classes.actionsContainer}
                    style={{
                        justifyContent: canCreateOrder ? 'space-between' : 'flex-end',
                    }}
                >
                    <div className={classes.flexRowSpaceBetween} style={{ marginTop: 2 }}>
                        {!orders.showSearch && isOpen && (
                            <Typography
                                variant="h6"
                                id="tableTitle"
                                component="span"
                                style={{ marginRight: 8 }}
                            >
                                {i18n.t('common.closed')}
                            </Typography>
                        )}
                        {isOpen && (
                            <Checkbox
                                color="primary"
                                style={{ padding: 4 }}
                                checked={showClosed}
                                onChange={() => {
                                    setShowClosed(!showClosed);
                                }}
                            />
                        )}
                    </div>
                    {canCreateOrder && !props.selectedOrders && (
                        <img
                            src={images.icons.circlePlusColored}
                            className={classes.addNewOrderIcon}
                            alt="add-icon"
                            onClick={() => setShowNewOrderModal(true)}
                        />
                    )}

                    {!props.selectedOrders && (
                        <img
                            id="date-time-picker"
                            src={images.icons.calendar}
                            alt="calendar-icon"
                            onClick={() => setShowPicker(!showPicker)}
                        />
                    )}
                    <DateTimePicker
                        futureTimeOnly
                        showButtons
                        isDateRange={true}
                        date={new Date(new Date().setUTCHours(0, 0, 0, 0))}
                        onSubmit={submitDateRange}
                        isOpen={showPicker}
                        onClose={() => setShowPicker(false)}
                    />
                </div>
            </Toolbar>

            <div className={classes.listContainer} ref={tableRef}>
                <TableContainer>
                    <Table stickyHeader aria-labelledby="tableTitle" aria-label="enhanced table">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    key={'qr'}
                                    align={'left'}
                                    className={classes.ordersTableHeader}
                                    sortDirection="asc"
                                >
                                    {!!props.onOrderSelected && (
                                        <Checkbox
                                            color={'primary'}
                                            style={{
                                                padding: '4px 4px 4px 4px',
                                                marginBottom: 2,
                                            }}
                                            checked={
                                                orders.orders.length > 0 &&
                                                orders.orders.length ===
                                                    props?.selectedOrders?.length
                                            }
                                            onChange={() => {
                                                if (props?.onOrderSelected) {
                                                    if (
                                                        orders.orders.length ===
                                                        props?.selectedOrders?.length
                                                    ) {
                                                        return props.onOrderSelected([]);
                                                    }
                                                    props.onOrderSelected([
                                                        ...orders.orders.map((order) => order.id),
                                                    ]);
                                                }
                                            }}
                                        />
                                    )}
                                    <TableSortLabel
                                        active={orders.orders.length > 0 && orderBy === 'qr'}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder('qr')}
                                    >
                                        {i18n.t('common.destination')}
                                    </TableSortLabel>
                                </TableCell>
                                {isOpen && (
                                    <TableCell
                                        key={'number'}
                                        align={'left'}
                                        className={classes.ordersTableHeader}
                                        sortDirection="asc"
                                        style={{ flexDirection: 'row' }}
                                    >
                                        <div style={{ display: 'flex' }}>
                                            <TableSortLabel
                                                active={
                                                    orders.orders.length > 0 && orderBy === 'number'
                                                }
                                                direction={asc ? 'asc' : 'desc'}
                                                onClick={() => handleOrder('number')}
                                            >
                                                {'№'}
                                            </TableSortLabel>
                                        </div>
                                    </TableCell>
                                )}

                                <TableCell
                                    key={'time'}
                                    align={'left'}
                                    className={classes.ordersTableHeader}
                                    sortDirection="asc"
                                >
                                    <TableSortLabel
                                        active={orders.orders.length > 0 && orderBy === 'time'}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder('time')}
                                    >
                                        {i18n.t('common.time')}
                                    </TableSortLabel>
                                </TableCell>

                                {!navBarOpen && isOpen && (
                                    <TableCell
                                        key={'service'}
                                        align={'center'}
                                        className={classes.ordersTableHeader}
                                        sortDirection="asc"
                                    >
                                        <TableSortLabel
                                            active={
                                                orders.orders.length > 0 && orderBy === 'service'
                                            }
                                            direction={asc ? 'asc' : 'desc'}
                                            onClick={() => handleOrder('service')}
                                        >
                                            {i18n.t('common.service')}
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {isOpen && (
                                    <TableCell
                                        key={'status'}
                                        align={'left'}
                                        className={classes.ordersTableHeader}
                                        sortDirection="asc"
                                        style={{
                                            paddingRight: 10,
                                        }}
                                    >
                                        <TableSortLabel
                                            active={
                                                orders.orders.length > 0 && orderBy === 'status'
                                            }
                                            direction={asc ? 'asc' : 'desc'}
                                            onClick={() => handleOrder('status')}
                                        >
                                            {i18n.t('common.status')}
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody style={{ maxHeight: '50%', overflow: 'auto' }}>
                            {prepareTableBody()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <ListItem style={{ flex: 0, padding: 16 }} onClick={() => setOpen(!isOpen)}>
                    <ListItemIcon className={classes.collapsedIconContainer}>
                        <div
                            className={clsx(classes.imageContainer, {
                                [classes.collapsedIcon]: !isOpen,
                            })}
                            style={{ backgroundImage: `url(${images.icons.collapseIcon})` }}
                        />
                    </ListItemIcon>
                </ListItem>
                <TablePagination
                    className={classes.pageBodyContainer}
                    rowsPerPageOptions={isOpen ? pagingOptions : []}
                    component="div"
                    count={orders.count}
                    rowsPerPage={paging.rowsPerPage}
                    labelRowsPerPage={''}
                    page={paging.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    style={{ flex: 1 }}
                    labelDisplayedRows={isOpen ? undefined : () => ''}
                />
            </Box>
            <AddNewOrderModal
                isOpen={showNewOrderModal}
                onClose={() => setShowNewOrderModal(false)}
            />
            <OrdersListSkeleton isLoading={isOrdersListLoading(orders, user)} />
        </Card>
    );
}
